import React, { useState, useEffect } from 'react';
import { parseDate, formatDateString, formatTimeString } from '../../../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  TextField,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AppService from '../../../Configurations/Appservice';
import { API_PATHS } from '../../../utils/constants';
// Use AppService instead of axios for proper authentication

// User-facing labels for payment statuses. WAITING_FOR_APPROVAL is shown as
// "In Review" because that's how it's described on the payments tile and
// what the user expects in this UI; the rest are sentence-cased for legibility.
const PAYMENT_STATUS_LABELS = {
  PAID: 'Paid',
  UNPAID: 'Unpaid',
  WAITING_FOR_APPROVAL: 'In Review',
  PAYMENT_REJECTED: 'Rejected',
  PENDING: 'Pending',
  PARTIAL: 'Partial',
  REFUNDED: 'Refunded',
};

// Status chip component with compact design
const StatusChip = ({ status }) => {
  const raw = String(status || '').toUpperCase();
  let color = 'default';

  switch (raw) {
    case 'COMPLETED':
    case 'PAID':
      color = 'success';
      break;
    case 'IN_PROGRESS':
    case 'WAITING_FOR_APPROVAL':
      color = 'warning';
      break;
    case 'CANCELLED':
    case 'REJECTED':
    case 'PAYMENT_REJECTED':
      color = 'error';
      break;
    case 'CREATED':
    case 'ASSIGNED':
      color = 'info';
      break;
    case 'UNPAID':
    case 'PENDING':
      color = 'default';
      break;
    default:
      color = 'default';
  }

  const label = PAYMENT_STATUS_LABELS[raw] || status || 'Unknown';

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{ height: 24, fontSize: '0.75rem' }}
    />
  );
};

// Workflow chip component with compact design
const WorkflowChip = ({ workflowName }) => {
  return workflowName ? (
      <Chip 
        label={workflowName} 
        color="primary" 
        size="small" 
        variant="outlined"
        sx={{ height: 24, fontSize: '0.75rem', maxWidth: 150 }}
      />    
  ) : (
    <Chip 
      label="Standalone" 
      color="default" 
      size="small" 
      variant="outlined"
      sx={{ height: 24, fontSize: '0.75rem' }}
    />
  );
};

const PendingTickets = ({ targetType: propTargetType, title: propTitle } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all'); // 'all' | 'PAID' | 'UNPAID' | 'IN_REVIEW'
  const [filterType, setFilterType] = useState('all'); // 'all', 'workflow', 'standalone'
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [userType, setUserType] = useState(null);
  const [userOrgId, setUserOrgId] = useState(null);

  const normalizedUserType = String(userType || '').toUpperCase();
  const principalInfo = (() => {
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      return authInfo.principalInfo || {};
    } catch (e) {
      return {};
    }
  })();
  const principalRoles = Array.isArray(principalInfo?.roles) ? principalInfo.roles : [];
  const normalizedPrincipalUserType = String(principalInfo?.userType || '').toUpperCase();
  const normalizedOrgName = String(principalInfo?.organisationName || '').toUpperCase();
  const isAdminUser =
    principalRoles.some((role) => String(role || '').toUpperCase().includes('ROOT ADMIN')) ||
    principalRoles.some((role) => String(role || '').toUpperCase().includes('THAKLIS')) ||
    normalizedPrincipalUserType.includes('ADMIN') ||
    normalizedUserType.includes('ADMIN') ||
    normalizedOrgName === 'THAKLIS';

  // Determine initial date range: admin = today (1 day), customer = last 30 days
  const isCustomerInit = normalizedPrincipalUserType.includes('CUSTOMER') ||
    principalRoles.some((role) => String(role || '').toUpperCase().includes('CUSTOMER'));
  const defaultEnd = new Date();
  const defaultStart = (() => {
    if (isCustomerInit) {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d;
    }
    return new Date();
  })();
  const defaultPreset = isCustomerInit ? 'last30days' : 'today';

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [dateRange, setDateRange] = useState({ preset: defaultPreset, startDate: defaultStart, endDate: defaultEnd });
  const showCustomerOrganisationColumn = Boolean(normalizedUserType) && !normalizedUserType.includes('CUSTOMER_ORG');

  const formatDateForApi = (date) => {
    if (!(date instanceof Date)) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const copyToClipboard = async (text) => {
    try {
      const v = text == null ? '' : String(text);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(v);
      } else {
        const ta = document.createElement('textarea');
        ta.value = v;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleDateRangePreset = (preset) => {
    const now = new Date();
    let start = null;
    let end = null;

    switch (preset) {
      case 'today':
        start = new Date();
        end = new Date();
        setStartDate(start);
        setEndDate(end);
        break;
      case 'last7days':
        start = new Date();
        start.setDate(now.getDate() - 7);
        end = new Date();
        setStartDate(start);
        setEndDate(end);
        break;
      case 'last30days':
        start = new Date();
        start.setDate(now.getDate() - 30);
        end = new Date();
        setStartDate(start);
        setEndDate(end);
        break;
      case 'last90days':
        start = new Date();
        start.setDate(now.getDate() - 90);
        end = new Date();
        setStartDate(start);
        setEndDate(end);
        break;
      case 'custom':
        setDateRange(prev => ({ ...prev, preset }));
        return;
      case 'allTime':
        // No date filter — backend returns all-time paginated results.
        setStartDate(null);
        setEndDate(null);
        setDateRange({ startDate: null, endDate: null, preset });
        return;
      default:
        break;
    }

    setDateRange({ startDate: start, endDate: end, preset });
  };

  // State for the ticket details dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicketData, setSelectedTicketData] = useState(null);
  const [downloadingTicketId, setDownloadingTicketId] = useState(null);

  const getAuthToken = () => {
    try {
      return (
        localStorage.getItem('jwt_token') ||
        JSON.parse(localStorage.getItem('authInfo') || '{}').token ||
        null
      );
    } catch (e) {
      return null;
    }
  };

  const handleDownloadAllDocuments = async (ticket) => {
    if (!ticket?.ticketNumber) {
      alert('Cannot download documents: ticket number is missing');
      return;
    }
    const token = getAuthToken();
    if (!token) {
      alert('Authentication required. Please log in again.');
      return;
    }

    setDownloadingTicketId(ticket.id);
    try {
      const listResp = await fetch(
        `${API_PATHS.FILE}/additional-documents/${ticket.ticketNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!listResp.ok) {
        throw new Error(`Failed to load documents (${listResp.status})`);
      }
      const docs = await listResp.json();
      if (!Array.isArray(docs) || docs.length === 0) {
        alert('No additional documents found for this ticket');
        return;
      }

      for (const doc of docs) {
        if (!doc.filePath) continue;
        const url = `${API_PATHS.BASE}/v1/files/download?filePath=${encodeURIComponent(doc.filePath)}`;
        try {
          const fileResp = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!fileResp.ok) {
            console.error(`Failed to download ${doc.originalName || doc.filePath}: ${fileResp.status}`);
            continue;
          }
          const blob = await fileResp.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = doc.originalName || doc.name || 'document';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
          console.error('Error downloading document:', doc, e);
        }
      }
    } catch (e) {
      console.error('Error downloading additional documents:', e);
      alert(`Error downloading documents: ${e.message}`);
    } finally {
      setDownloadingTicketId(null);
    }
  };

  // Function to handle API errors with fallback
  const handleApiError = (error, fallbackAction, errorMessage) => {
    console.error(errorMessage, error);
    setError(error.message || 'An unexpected error occurred');
    
    // Execute fallback action if provided
    if (fallbackAction) {
      fallbackAction();
    }
  };

  // Get user info from localStorage with more robust error handling
  const getUserInfo = () => {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) return JSON.parse(userInfoStr);

      const authInfoStr = localStorage.getItem('authInfo');
      if (authInfoStr) return JSON.parse(authInfoStr);
    } catch (error) {
      console.error('Error parsing user info:', error);
    }
    return null;
  };
  
  // Initialize user type and organization ID
  useEffect(() => {
    const userInfo = getUserInfo();
    if (userInfo) {
      // Determine user type
      const type = userInfo.principalInfo?.userType ||
                  userInfo.user?.userType ||
                  userInfo.userType ||
                  'UNKNOWN';
      const normalizedType = String(type || 'UNKNOWN').toUpperCase();
      setUserType(normalizedType);

      // Get organization ID
      const orgId = userInfo.principalInfo?.organisationId ||
                   userInfo.organisation?.id ||
                   userInfo.user?.organisationId ||
                   userInfo.orgId;
      setUserOrgId(orgId);

      console.log(`User type: ${type}, Organization ID: ${orgId}`);

      // For customer users, automatically set the selected organization
      if (normalizedType.includes('CUSTOMER') && orgId) {
        setSelectedOrg(orgId);
      }

      // Date range is already initialized correctly based on user type (see defaultPreset above)
    }
  }, []);

  // Function to fetch tickets from the API
  const fetchTickets = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      let ticketsData = [];
      let workflowInstancesData = [];
      let organizationsData = [];

      const ticketParams = {
        page,
        pageSize,
      };

      // If a targetType prop is passed, always use it (e.g., ORGANIZATION for org tickets page)
      // Otherwise, for non-admin users, default to EMPLOYEE
      if (propTargetType) {
        ticketParams.targetType = propTargetType;
      } else if (!isAdminUser) {
        ticketParams.targetType = 'EMPLOYEE';
      }

      const PRESET_DAYS = { today: 1, last7days: 7, last30days: 30, last90days: 90 };

      if (dateRange.preset === 'allTime') {
        // no date params — backend returns all-time paginated results
      } else if (dateRange.preset !== 'custom' && PRESET_DAYS[dateRange.preset]) {
        ticketParams.days = PRESET_DAYS[dateRange.preset];
      } else {
        if (startDate) {
          ticketParams.fromDate = formatDateForApi(startDate);
        }
        if (endDate) {
          ticketParams.toDate = formatDateForApi(endDate);
        }
      }

      // customerOrgId must only be sent for admin users.
      // For customer-org users we rely on backend auth + frontend safety filter below.
      if (isAdminUser && selectedOrg && selectedOrg !== 'all') {
        ticketParams.customerOrgId = selectedOrg;
      }

      // Status + payment status — server-side filtering. UNPAID on the backend
      // includes PAYMENT_REJECTED so the user doesn't have to think about it.
      if (filterStatus && filterStatus !== 'all') {
        ticketParams.status = filterStatus;
      }
      if (filterPaymentStatus && filterPaymentStatus !== 'all') {
        ticketParams.paymentStatus = filterPaymentStatus;
      }
      
      // Use Promise.allSettled to handle multiple API calls that might fail independently
      const results = await Promise.allSettled([
        // 1. Try to get tickets with the new endpoint that includes workflow information
        AppService.get('/v1/ticket/allWithWorkflowInfo', { params: ticketParams }),
        // 2. Try to get organizations with required type parameter using AppService
        // Only fetch organizations for admin users
        isAdminUser ? AppService.get('/v1/organisation?type=CUSTOMER') : Promise.resolve([])
      ]);
      console.info('results :', results);
      // Log that we're skipping the workflow instances API call
      console.info('Note: Workflow data is not being loaded due to backend authentication issues.');
      
      
      // Process tickets result
      if (results[0].status === 'fulfilled') {
        console.log('Fetched tickets from API:', results[0].value);
        // Handle different response formats
        if (results[0].value && results[0].value.data) {
          ticketsData = results[0].value.data;
        } else if (Array.isArray(results[0].value)) {
          ticketsData = results[0].value;
        } else if (results[0].value && results[0].value.content) {
          ticketsData = results[0].value.content;
        } else {
          // Fallback to empty array if we can't determine the format
          console.warn('Unexpected response format for tickets:', results[0].value);
          ticketsData = [];
        }
      } else {
        console.warn('Error fetching tickets:', results[0].reason);
        // No dummy data - just leave the array empty
        ticketsData = [];
        setError('Unable to load ticket data. Please try again later.');
      }
      
      // Ensure ticketsData is always an array
      if (!Array.isArray(ticketsData)) {
        console.warn('ticketsData is not an array, resetting to empty array');
        ticketsData = [];
      }

      setHasNextPage(ticketsData.length === pageSize);

      // Enhance ticket data with workflow information
      ticketsData = ticketsData.map(ticket => {
        // Check if this ticket is part of a workflow
        
        // If the ticket has a workflowInstanceId but we couldn't find the instance,
        // still mark it as part of a workflow with default values
        if (ticket && ticket.workflowInstanceId) {
          return {
            ...ticket,
            workflowName: ticket.workflowName || 'Unknown Workflow',
            requestId: ticket.workflowInstanceId,
            requestTitle: ticket.requestTitle || 'Workflow Request',
            isPartOfWorkflow: true
          };
        }
        
        // Default case: standalone ticket
        return {
          ...ticket,
          isPartOfWorkflow: false
        };
      });
      
      // Log workflow ticket counts
      const workflowTickets = ticketsData.filter(t => t.isPartOfWorkflow);
      console.log(`Found ${workflowTickets.length} workflow tickets out of ${ticketsData.length} total tickets`);

      // Process organizations result - only for admin users
      if (isAdminUser && results[1].status === 'fulfilled') {
        console.log('Fetched organizations from API:', results[1].value);
        // Handle different response formats
        if (results[1].value && results[1].value.data) {
          organizationsData = results[1].value.data;
        } else if (Array.isArray(results[1].value)) {
          organizationsData = results[1].value;
        } else if (results[1].value && results[1].value.content) {
          organizationsData = results[1].value.content;
        } else {
          // Fallback to empty array if we can't determine the format
          console.warn('Unexpected response format for organizations:', results[1].value);
          organizationsData = [];
        }
        
        // Ensure organizationsData is an array
        if (!Array.isArray(organizationsData)) {
          console.warn('organizationsData is not an array, resetting to empty array');
          organizationsData = [];
        }
        
        setOrganizations(organizationsData);
      } else if (isAdminUser) {
        console.warn('Error fetching organizations:', results[1].reason);
        // No dummy data - just leave the array empty
        setOrganizations([]);
      } else {
        // For non-admin users, we don't need to show all organizations
        console.log('Non-admin user, not fetching all organizations');
        setOrganizations([]);
      }
      
      // Update state with our processed data
      setTickets(ticketsData);
      
      // If all API calls failed, show an error
      if (results.every(result => result.status === 'rejected')) {
        setError('Unable to connect to the server. Please check your connection and try again.');
      }
    } catch (error) {
      console.error('Error in fetchTickets:', error);
      setError('Failed to load ticket data. Please try again later.');
      setTickets([]);
      setOrganizations([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial data loading — status / paymentStatus / preset are now part of the
  // server query, so refetch when they change.
  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedOrg, page, filterStatus, filterPaymentStatus, dateRange.preset]);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, selectedOrg, filterType, filterStatus, filterPaymentStatus, dateRange.preset]);

  // Function to handle refresh button click
  const handleRefresh = () => {
    fetchTickets(true);
  };

  // Function to handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Map tab index to filter type
    switch (newValue) {
      case 1:
        setFilterType('workflow');
        break;
      case 2:
        setFilterType('standalone');
        break;
      case 0:
      default:
        setFilterType('all');
        break;
    }
  };

  // Function to handle view ticket details
  const handleViewTicket = (ticketId) => {
    console.log('View ticket called with ID:', ticketId);
    
    if (!ticketId) {
      console.error('Invalid ticketId provided');
      return;
    }
    
    // Find the ticket in our data
    const selectedTicket = tickets.find(ticket => ticket.id === ticketId);
    
    if (!selectedTicket) {
      console.error('Ticket not found:', ticketId);
      return;
    }
    
    // Navigate to our new ticket detail view with assignment functionality
    navigate(`/ADM-Ticket/ticket-detail-view`, {
      state: {
        ticketId: ticketId,
        ticket: selectedTicket
      }
    });
  };

  // Function to handle view workflow
  const handleViewWorkflow = (requestId) => {
    navigate(`/ADM-Workflow/instance/${requestId}`);
  };

  // Filter tickets based on search term, status, and type
  const filteredTickets = tickets.filter(ticket => {
    if (!ticket) return false;
    
    try {
      // Apply search term filter - ensure all values are strings
      const ticketNumber = String(ticket.ticketNumber || '');
      const serviceName = String(ticket.serviceName || ticket.service?.name || '');
      const status = String(ticket.status || '');
      const workflowName = String(ticket.workflowName || '');
      const customerName = String(ticket.customerName || ticket.customer?.name || '');
      
      // Convert search term to string and lowercase once
      const searchTermLower = String(searchTerm).toLowerCase();
      
      const matchesSearch =
        ticketNumber.toLowerCase().includes(searchTermLower) ||
        serviceName.toLowerCase().includes(searchTermLower) ||
        status.toLowerCase().includes(searchTermLower) ||
        workflowName.toLowerCase().includes(searchTermLower) ||
        customerName.toLowerCase().includes(searchTermLower);

      // Status filter is applied server-side now; no client matchesStatus.

      // Apply type filter
      const matchesType =
        filterType === 'all' ||
        (filterType === 'workflow' && ticket.isPartOfWorkflow === true) ||
        (filterType === 'standalone' && ticket.isPartOfWorkflow !== true);

      return matchesSearch && matchesType;
    } catch (error) {
      console.error('Error filtering ticket:', error, ticket);
      return false;
    }
  });

  // Selectable statuses for the dropdown. value = backend TicketStatus enum
  // name (must match exactly or the request fails conversion); label is the
  // user-facing string. ASSIGNED_TO_ORGANIZATION is intentionally omitted.
  const statusOptions = [
    { value: 'OPEN', label: 'Open' },
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'TAKEN', label: 'Taken' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'INCOMPLETE', label: 'Incomplete' },
    { value: 'MODIFICATION_REQUIRED', label: 'Modification Required' },
  ];

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Loading tickets...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1">
          {propTitle || 'All Tickets'}
        </Typography>
        <Button 
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          size="small"
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main',
            fontWeight: 600,
            '& .MuiSvgIcon-root': { color: 'primary.main' },
            '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
            '&.Mui-disabled': {
              color: 'primary.main',
              borderColor: 'primary.main',
              opacity: 0.5,
            },
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="All Tickets" />
            <Tab label="Workflow Tickets" />
            <Tab label="Standalone Tickets" />
          </Tabs>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                  Date Range
                </Typography>
                {[
                  { key: 'today', label: '24hr' },
                  { key: 'last7days', label: '7 Days' },
                  { key: 'last30days', label: '30 Days' },
                  { key: 'last90days', label: '90 Days' },
                  { key: 'allTime', label: 'All Time' },
                  { key: 'custom', label: 'Custom' }
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    size="small"
                    variant={dateRange.preset === key ? 'contained' : 'outlined'}
                    onClick={() => handleDateRangePreset(key)}
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1,
                      minWidth: 'auto',
                      ...(dateRange.preset === key
                        ? {
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#1b5e20' },
                          }
                        : {
                            borderColor: '#2e7d32',
                            color: '#2e7d32',
                            '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                          }),
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateRange.preset === 'custom' && (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1}>
                  <DatePicker
                    label="From"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { fontSize: '0.8rem' } } } }}
                  />
                  <DatePicker
                    label="To"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { fontSize: '0.8rem' } } } }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search tickets..."
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1, minWidth: '200px', maxWidth: '400px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: '150px' }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {statusOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: '170px' }}>
            <InputLabel id="payment-status-filter-label">Payment</InputLabel>
            <Select
              labelId="payment-status-filter-label"
              id="payment-status-filter"
              value={filterPaymentStatus}
              label="Payment"
              onChange={(e) => setFilterPaymentStatus(e.target.value)}
            >
              <MenuItem value="all">All Payments</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="UNPAID">Unpaid</MenuItem>
              <MenuItem value="IN_REVIEW">In Review</MenuItem>
            </Select>
          </FormControl>
          
          {isAdminUser && (
            <FormControl size="small" sx={{ minWidth: '200px', mr: 2 }}>
              <InputLabel id="organization-filter-label">Organization</InputLabel>
              <Select
                labelId="organization-filter-label"
                id="organization-filter"
                value={selectedOrg}
                label="Organization"
                onChange={(e) => setSelectedOrg(e.target.value)}
              >
                <MenuItem value="all">All Organizations</MenuItem>
                {organizations.map(org => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {filteredTickets.length} tickets found
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Ticket ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Service</TableCell>
                {showCustomerOrganisationColumn && (
                  <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Customer Organization</TableCell>
                )}
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Employee</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Created Date</TableCell>
                {isAdminUser ? (
                  <>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Govt Fee</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1, lineHeight: 1.2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span>Service Fee</span>
                        <span>Partner Fee</span>
                        <span>CP Commission</span>
                        <span>Profit</span>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Cost (AED)</TableCell>
                )}
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Ticket Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Payment Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Workflow</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9 + (showCustomerOrganisationColumn ? 1 : 0) + (isAdminUser ? 1 : 0)}
                    align="center"
                  >
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        No tickets found matching your criteria.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => {
                  // Handle different API response formats
                  const ticketId = ticket.id;
                  // Prefer orgTicketNumber (organization-specific) over ticketNumber (global)  
                  const ticketNumber = ticket.orgTicketNumber || ticket.ticketNumber || `TK-${ticketId.substring(0, 5)}`;
                  const serviceName = ticket.serviceName || ticket.service?.name || 'Unknown Service';
                  const transactionNumber = ticket.transactionNumber || '-';
                  // Try to get customer name from static fields first, fall back to other sources
                  const staticFields = ticket.staticFields || [];
                  const firstNameField = staticFields.find(field => field.code === 'firstNameEn');
                  const customerName = firstNameField?.value || ticket.customerName || ticket.customer?.name || 'Unknown Customer';
                  const customerOrganisation = ticket.customerOrganisation || '—';
                  const createdDate = ticket.createDateTime || ticket.createdAt || new Date().toISOString();
                  const status = ticket.status || 'PENDING';
                  
                  return (
                    <TableRow key={ticketId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          {ticketNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          {serviceName}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s ease-in-out' },
                            '&:hover .copy-icon': { opacity: 1 },
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem', fontWeight: 400, lineHeight: 1.2 }}
                          >
                            {transactionNumber}
                          </Typography>
                          <Tooltip title="Copy transaction number">
                            <IconButton
                              className="copy-icon"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(transactionNumber);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {ticket.stepName && (
                          <Typography variant="caption" color="textSecondary">
                            Step: {ticket.stepName}
                          </Typography>
                        )}
                      </TableCell>
                      {showCustomerOrganisationColumn && (
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s ease-in-out' },
                              '&:hover .copy-icon': { opacity: 1 },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                              {customerOrganisation}
                            </Typography>
                            <Tooltip title="Copy customer organization">
                              <IconButton
                                className="copy-icon"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(customerOrganisation);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s ease-in-out' },
                            '&:hover .copy-icon': { opacity: 1 },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {customerName.toUpperCase()}
                          </Typography>
                          <Tooltip title="Copy employee">
                            <IconButton
                              className="copy-icon"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(customerName.toUpperCase());
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          {formatDateString(createdDate)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTimeString(createdDate)}
                        </Typography>
                      </TableCell>
                      {isAdminUser ? (() => {
                        // Root-admin breakdown. Service fee = serviceCharge + tax;
                        // partner fee = what the partner is paid; CP commission = the
                        // channel-partner commission snapshotted at assignment (blank
                        // when the customer isn't referred); profit = Thaklis's cut on
                        // this ticket (service fee minus partner fee minus CP commission).
                        const sc = ticket.serviceCharge != null ? Number(ticket.serviceCharge) : null;
                        const tx = ticket.tax != null ? Number(ticket.tax) : null;
                        const pf = ticket.partnerServiceFee != null ? Number(ticket.partnerServiceFee) : null;
                        const cp = ticket.channelPartnerCommission != null ? Number(ticket.channelPartnerCommission) : null;
                        const serviceFee = sc != null || tx != null ? (sc || 0) + (tx || 0) : null;
                        const profit = serviceFee != null && pf != null ? serviceFee - pf - (cp || 0) : null;
                        const fmt = (v) => (v != null ? v.toFixed(2) : '-');
                        return (
                          <>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                {ticket.governmentFees != null ? Number(ticket.governmentFees).toFixed(2) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{fmt(serviceFee)}</Typography>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{fmt(pf)}</Typography>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{fmt(cp)}</Typography>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{fmt(profit)}</Typography>
                              </Box>
                            </TableCell>
                          </>
                        );
                      })() : (
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                            {ticket.totalCost != null ? Number(ticket.totalCost).toFixed(2) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <StatusChip status={status} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={ticket.paymentStatus || 'N/A'} />
                      </TableCell>
                      <TableCell>
                        <WorkflowChip 
                          workflowName={ticket.workflowName} 
                          workflowId={ticket.workflowId} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Ticket Details">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewTicket(ticketId)}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Additional Documents">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => handleDownloadAllDocuments(ticket)}
                              size="small"
                              disabled={downloadingTicketId === ticketId}
                            >
                              {downloadingTicketId === ticketId
                                ? <CircularProgress size={16} />
                                : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>

                        {ticket.isPartOfWorkflow && ticket.requestId && (
                          <Tooltip title="View Workflow">
                            <IconButton
                              color="secondary"
                              onClick={() => handleViewWorkflow(ticket.requestId)}
                              size="small"
                            >
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading || refreshing}
            sx={{
              minWidth: 90,
              height: 30,
              px: 2,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: 0.2,
              textTransform: 'none',
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.25)',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.55)',
                backgroundColor: 'rgba(46, 125, 50, 0.08)',
                color: 'text.primary',
              },
              '&.Mui-disabled': {
                opacity: 1,
                color: 'rgba(0,0,0,0.35)',
                borderColor: 'rgba(0,0,0,0.15)',
                backgroundColor: '#fff',
              },
            }}
          >
            Prev
          </Button>
          <Typography variant="caption" color="textSecondary">
            Page {page + 1}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasNextPage || loading || refreshing}
            sx={{
              minWidth: 90,
              height: 30,
              px: 2,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: 0.2,
              textTransform: 'none',
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.25)',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.55)',
                backgroundColor: 'rgba(46, 125, 50, 0.08)',
                color: 'text.primary',
              },
              '&.Mui-disabled': {
                opacity: 1,
                color: 'rgba(0,0,0,0.35)',
                borderColor: 'rgba(0,0,0,0.15)',
                backgroundColor: '#fff',
              },
            }}
          >
            Next
          </Button>
        </Box>

      </Paper>
    </Box>
    </LocalizationProvider>
  );
};

export default PendingTickets;
